/**
 * Device check card (11/2/2026): the office's view of whether a device's
 * record is proven against its tag — Checked / Can't read / Not checked —
 * with the tag photo, who and when, every before → after change to make,
 * model, size, type or serial (and whether it cleared the check), and what
 * ServiceTitan wanted to change that we kept. Every fact shows its proof.
 * Reads GET /api/assets/:id/device-check; changes nothing.
 */
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, CircleHelp } from "lucide-react";
import { api } from "@/lib/api";
import { cn, formatDateTime } from "@/lib/utils";

export interface DeviceCheckData {
    enabled: boolean;
    state: "checked" | "cant_read" | "not_checked";
    checked: { at: string; byName: string | null; method: string; photoId: string | null } | null;
    cantRead: { at: string; byName: string | null; reason: string; reasonLabel: string | null } | null;
    photos: Array<{ id: string; kind: string; at: string; takenByName: string | null; url: string }>;
    history: Array<{
        at: string;
        source: string;
        actorName: string | null;
        before: Record<string, string | null>;
        after: Record<string, string | null>;
        checkCleared: boolean;
    }>;
    stSkipped: Array<{ field: string; ours: string | null; theirs: string | null; lastSeenAt: string }>;
}

const FIELD_WORDS: Record<string, string> = {
    manufacturer: "Make",
    model: "Model",
    size_label: "Size",
    assembly_type: "Type",
    serial_number: "Serial",
    name: "Label",
};
const SOURCE_WORDS: Record<string, string> = {
    tech: "tech, at the device",
    edit: "edit",
    office_match: "office, from photos",
    st_sync: "ServiceTitan sync",
    import: "import",
    unknown: "system",
};
const PHOTO_WORDS: Record<string, string> = {
    nameplate: "Tag",
    whole_device: "Whole device",
    body_casting: "Body casting",
    check_covers: "Check covers",
    lay_length: "Lay length",
    bypass_tag: "Bypass tag",
};

export function useDeviceCheck(assetId: string | null | undefined) {
    return useQuery({
        queryKey: ["device-check", assetId],
        queryFn: () => api.get<DeviceCheckData>(`/api/assets/${assetId}/device-check`),
        enabled: !!assetId,
        staleTime: 30_000,
    });
}

export function DeviceCheckChip({ data }: { data: DeviceCheckData | undefined }) {
    if (!data?.state) return null;
    const look =
        data.state === "checked"
            ? { cls: "bg-emerald-100 text-emerald-900", icon: CheckCircle2, text: "Checked against the tag" }
            : data.state === "cant_read"
              ? { cls: "bg-amber-100 text-amber-900", icon: CircleHelp, text: "Can't read the tag — office to match" }
              : { cls: "bg-red-100 text-red-900", icon: CircleAlert, text: "Not checked — no parts orders" };
    const Icon = look.icon;
    return (
        <span
            className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold", look.cls)}
            data-testid="device-check-chip"
            data-state={data.state}
        >
            <Icon className="h-3.5 w-3.5" /> {look.text}
        </span>
    );
}

export function DeviceCheckCard({ assetId }: { assetId: string }) {
    const q = useDeviceCheck(assetId);
    const d = q.data;
    if (!d) return null;
    const tag = d.photos.find((p) => p.kind === "nameplate") ?? null;
    return (
        <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="device-check-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-bold">Device check</div>
                <DeviceCheckChip data={d} />
            </div>
            <div className="text-xs text-foreground/70">
                {d.checked
                    ? `Checked ${formatDateTime(d.checked.at)}${d.checked.byName ? ` by ${d.checked.byName}` : ""}${
                          d.checked.method === "office_from_photos" ? " (office, from photos)" : ""
                      }. It stays checked until someone changes make, model, size, type or serial.`
                    : d.cantRead
                      ? `${d.cantRead.reasonLabel ?? "Can't read the tag"} — ${formatDateTime(d.cantRead.at)}${
                            d.cantRead.byName ? ` by ${d.cantRead.byName}` : ""
                        }. Parts wait until the office matches the photos to a model.`
                      : "Nobody has checked this record against the tag yet. The next tech here takes a tag photo."}
            </div>
            {d.photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {(tag ? [tag, ...d.photos.filter((p) => p !== tag)] : d.photos).map((p) => (
                        <a
                            key={p.id}
                            href={p.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-28 overflow-hidden rounded-md border text-[11px]"
                        >
                            <img src={p.url} alt={PHOTO_WORDS[p.kind] ?? p.kind} className="h-20 w-full object-cover" />
                            <div className="px-1.5 py-1 font-semibold">{PHOTO_WORDS[p.kind] ?? p.kind}</div>
                        </a>
                    ))}
                </div>
            )}
            {d.stSkipped.length > 0 && (
                <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
                    <div className="font-bold">ServiceTitan says something different. We kept ours.</div>
                    {d.stSkipped.map((s) => (
                        <div key={`${s.field}-${s.theirs}`}>
                            {FIELD_WORDS[s.field] ?? s.field}: ServiceTitan says <b>{s.theirs}</b>, we have{" "}
                            <b>{s.ours ?? "—"}</b>
                        </div>
                    ))}
                </div>
            )}
            {d.history.length > 0 && (
                <div className="space-y-1">
                    <div className="text-xs font-bold uppercase tracking-wide text-foreground/60">Changes</div>
                    {d.history.map((h, i) => (
                        <div key={i} className="text-xs" data-testid="device-check-history-row">
                            <span className="text-foreground/60">
                                {formatDateTime(h.at)} · {h.actorName ?? SOURCE_WORDS[h.source] ?? h.source}
                            </span>{" "}
                            {Object.keys(h.after).map((k) => (
                                <span key={k} className="mr-2">
                                    {FIELD_WORDS[k] ?? k}: <s className="text-red-700">{h.before[k] ?? "—"}</s> →{" "}
                                    <b>{h.after[k] ?? "—"}</b>
                                </span>
                            ))}
                            {h.checkCleared && (
                                <span className="rounded bg-red-100 px-1 font-semibold text-red-900">
                                    check cleared
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
